import React, { useState, useEffect, useCallback } from "react";

const PurchaseFlowModal = ({ isOpen, onClose, axiosInstance }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Step 1: Plan Selection Data
  const [userPlans, setUserPlans] = useState(null);
  const [selectedUserPlan, setSelectedUserPlan] = useState(null);

  const [modules, setModules] = useState(null);

  const [selectedModuleIds, setSelectedModuleIds] = useState([]);

  // Step 1.5: Pricing Plan Data (after module selection)
  // Step 1.5: Pricing Plan Data (after plan selection)
  const [pricingPlans, setPricingPlans] = useState(null);
  const [selectedPricingModules, setSelectedPricingModules] = useState([]);

  // Step 2: Company & User Details
  const [companyDetails, setCompanyDetails] = useState({
    companyStrength: 0,
    adminUsers: 0,
  });

  // Step 3: Quotation Data
  const [quotation, setQuotation] = useState(null);

  // Step 4: Final Details
  const [finalDetails, setFinalDetails] = useState({
    companyName: "",
    email: "",
    userName: "",
    role: "ADMIN",
    subDomain: "",
    monthPlan: "THREE_MONTHS",
    purchasedAt: new Date().toISOString().split("T")[0],
    validTill: "",
    loginCredentialDTO: {
      email: "",
      password: "",
      name: "",
    },
    amountPaid: 0,
  });

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansResponse, modulesResponse] = await Promise.all([
        axiosInstance.get("/user-plans"),
        axiosInstance.get("/modules"),
      ]);

      if (
        plansResponse.data.status === 0 ||
        plansResponse.data.status === 200
      ) {
        setUserPlans(plansResponse.data.data);
      }

      if (
        modulesResponse.data.status === 0 ||
        modulesResponse.data.status === 200
      ) {
        setModules(modulesResponse.data.data);
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      alert("Failed to load plans and modules.");
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  // Fetch pricing plans when user plan is selected
  const fetchPricingPlans = async () => {
    if (!selectedUserPlan || selectedModuleIds.length === 0) {
      alert("Please select a user plan and at least one module");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        userPlanId: selectedUserPlan.userPlanId,
        moduleIds: selectedModuleIds,
      };

      console.log("Fetching pricing plans:", payload);

      const response = await axiosInstance.post(
        "/purchase/pricing-plan",
        payload,
      );

      console.log("Pricing plan response:", response.data);

      if (response.data.status === 0 || response.data.status === 200) {
        setPricingPlans(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching pricing plans:", error);
      alert("Failed to load pricing plans.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && currentStep === 1 && (!userPlans || !modules)) {
      fetchInitialData();
    }
  }, [isOpen, currentStep, userPlans, modules, fetchInitialData]);

  const handleModuleSelection = (moduleId) => {
    setSelectedModuleIds((prev) => {
      if (prev.includes(moduleId)) {
        return prev.filter((id) => id !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };

  const handlePricingModuleToggle = (module) => {
    setSelectedPricingModules((prev) => {
      const exists = prev.find((m) => m.moduleId === module.moduleId);
      if (exists) {
        return prev.filter((m) => m.moduleId !== module.moduleId);
      } else {
        return [...prev, module];
      }
    });
  };

  const fetchQuotation = async () => {
    setLoading(true);
    try {
      const modulePricePlanIds = selectedPricingModules.map(
        (m) => m.modulePricingPlanId,
      );

      const payload = {
        modulePricePlanId: modulePricePlanIds,
        companyStrength: companyDetails.companyStrength,
        adminLoginRequired: companyDetails.adminUsers,
      };

      console.log("Fetching quotation:", payload);

      const response = await axiosInstance.post("/purchase/quotation", payload);

      console.log("Quotation response:", response.data);

      if (response.data.status === 0 || response.data.status === 200) {
        setQuotation(response.data.data);
        calculateValidTill(finalDetails.monthPlan);
      }
    } catch (error) {
      console.error("Quotation API failed:", error);
      alert("Failed to generate quotation");
    } finally {
      setLoading(false);
    }
  };

  const calculateValidTill = (monthPlan) => {
    const purchaseDate = new Date(finalDetails.purchasedAt);
    let months = 3;

    if (monthPlan === "SIX_MONTHS") months = 6;
    else if (monthPlan === "TWELVE_MONTHS") months = 12;

    purchaseDate.setMonth(purchaseDate.getMonth() + months);
    setFinalDetails((prev) => ({
      ...prev,
      validTill: purchaseDate.toISOString().split("T")[0],
    }));
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!selectedUserPlan) {
        alert("Please select a user plan");
        return;
      }
      if (selectedModuleIds.length === 0) {
        alert("Please select at least one module");
        return;
      }
      fetchPricingPlans();
    }

    if (currentStep === 2) {
      if (selectedPricingModules.length === 0) {
        alert("Please select at least one pricing module");
        return;
      }
    }

    if (currentStep === 3) {
      if (
        !companyDetails.companyStrength ||
        companyDetails.companyStrength < 1
      ) {
        alert("Please enter company strength");
        return;
      }
      if (selectedUserPlan) {
        if (companyDetails.companyStrength < selectedUserPlan.minimumUser) {
          alert(
            `Company strength must be at least ${selectedUserPlan.minimumUser} users for this plan`,
          );
          return;
        }
        if (
          selectedUserPlan.maximumUser > 0 &&
          companyDetails.companyStrength > selectedUserPlan.maximumUser
        ) {
          alert(
            `Company strength cannot exceed ${selectedUserPlan.maximumUser} users for this plan`,
          );
          return;
        }
      }
      fetchQuotation();
    }

    setCurrentStep((prev) => prev + 1);
  };

  const handlePreviousStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    // Validation
    if (
      !finalDetails.companyName ||
      !finalDetails.email ||
      !finalDetails.userName
    ) {
      alert("Please fill company details");
      return;
    }
    if (!finalDetails.subDomain) {
      alert("Please enter subdomain");
      return;
    }
    if (
      !finalDetails.loginCredentialDTO.email ||
      !finalDetails.loginCredentialDTO.password ||
      !finalDetails.loginCredentialDTO.name
    ) {
      alert("Please fill all admin details");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        companyName: finalDetails.companyName,
        email: finalDetails.email,
        userName: finalDetails.userName,
        role: finalDetails.role,
        loginCredentialDTO: finalDetails.loginCredentialDTO,
        subDomain: finalDetails.subDomain,
        monthPlan: finalDetails.monthPlan,
        purchasedAt: finalDetails.purchasedAt,
        validTill: finalDetails.validTill,
        modulepricingPlanIds: selectedPricingModules.map(
          (m) => m.modulePricingPlanId,
        ),
        companyStrength: companyDetails.companyStrength,
        baseUserPlan: quotation.baseUserPlan,
        adminLoginRequired: companyDetails.adminUsers,
        adminUserPricePerUser: quotation.adminUserPricePerUser,
        pricePerUser:
          quotation.totalPrice / companyDetails.companyStrength || 0,
        serverCost: quotation.serverCost,
        totalAmmount: quotation.totalPrice,
        amountPaid: finalDetails.amountPaid,
      };

      console.log("Creating tenant:", payload);

      await axiosInstance.post("/purchase/new-tenant", payload);
      alert("Tenant created successfully!");
      onClose();
      window.location.reload();
    } catch (error) {
      console.error("Error creating tenant:", error);
      alert("Failed to create tenant. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <button style={styles.closeBtn} onClick={onClose}>
          ×
        </button>

        {/* Progress Indicator */}
        <div style={styles.progressBar}>
          {[1, 2, 3, 4, 5].map((step) => (
            <div
              key={step}
              style={{
                ...styles.progressStep,
                ...(currentStep >= step ? styles.progressStepActive : {}),
              }}
            >
              {step}
            </div>
          ))}
        </div>

        {loading && (
          <div style={styles.loadingOverlay}>
            <div style={styles.spinner}></div>
          </div>
        )}

        {/* Step 1: Plan and Module Selection */}
        {currentStep === 1 && (
          <div style={styles.stepContent}>
            <h2 style={styles.stepTitle}>Select Your Plan & Modules</h2>

            {/* User Plans Section */}
            <div style={styles.section}>
              <h3 style={styles.sectionSubtitle}>Choose User Plan</h3>

              <div style={styles.userPlansGrid}>
                {userPlans &&
                  userPlans.map((plan) => (
                    <div
                      key={plan.userPlanId}
                      style={{
                        ...styles.planCard,
                        ...(selectedUserPlan?.userPlanId === plan.userPlanId
                          ? styles.planCardSelected
                          : {}),
                      }}
                      onClick={() => setSelectedUserPlan(plan)}
                    >
                      <div style={styles.planHeader}>
                        <input
                          type="radio"
                          name="userPlan"
                          checked={
                            selectedUserPlan?.userPlanId === plan.userPlanId
                          }
                          onChange={() => setSelectedUserPlan(plan)}
                          style={styles.radio}
                        />
                        <div style={styles.planInfo}>
                          <div style={styles.planDetails}>
                            {plan.minimumUser} –{" "}
                            {plan.maximumUser > 0
                              ? plan.maximumUser
                              : "Unlimited"}{" "}
                            users
                          </div>
                          {plan.basePlan && (
                            <span style={styles.badge}>Base Plan</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Modules Section - Show modules from /modules API */}
            {modules && (
              <div style={styles.section}>
                <h3 style={styles.sectionSubtitle}>Select Modules</h3>

                <div style={styles.modulesGrid}>
                  {modules.map((module) => (
                    <div
                      key={module.moduleId}
                      style={{
                        ...styles.moduleCard,
                        ...(selectedModuleIds.includes(module.moduleId)
                          ? styles.moduleCardSelected
                          : {}),
                      }}
                      onClick={() => handleModuleSelection(module.moduleId)}
                    >
                      <label style={styles.moduleLabel}>
                        <input
                          type="checkbox"
                          checked={selectedModuleIds.includes(module.moduleId)}
                          onChange={() =>
                            handleModuleSelection(module.moduleId)
                          }
                          style={styles.checkbox}
                        />
                        <div style={styles.moduleContent}>
                          <div style={styles.moduleName}>
                            {module.moduleName}
                          </div>
                          {module.moduleDescription && (
                            <div style={styles.moduleDescription}>
                              {module.moduleDescription}
                            </div>
                          )}
                          {module.moduleFeatures &&
                            module.moduleFeatures.length > 0 && (
                              <ul style={styles.featureList}>
                                {module.moduleFeatures
                                  .slice(0, 3)
                                  .map((feature) => (
                                    <li
                                      key={feature.moduleFeatureId}
                                      style={styles.featureItem}
                                    >
                                      {feature.featureName}
                                    </li>
                                  ))}
                                {module.moduleFeatures.length > 3 && (
                                  <li style={styles.featureItem}>
                                    +{module.moduleFeatures.length - 3} more
                                    features
                                  </li>
                                )}
                              </ul>
                            )}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!modules && (
              <div style={styles.loadingText}>Loading modules...</div>
            )}

            <button
              onClick={handleNextStep}
              style={styles.nextBtn}
              disabled={!selectedUserPlan || selectedModuleIds.length === 0}
            >
              Next: View Pricing
            </button>
          </div>
        )}

        {/* Step 2: Pricing Plans - Select modules with pricing */}
        {currentStep === 2 && pricingPlans && (
          <div style={styles.stepContent}>
            <h2 style={styles.stepTitle}>Select Module Plans</h2>

            {selectedUserPlan && (
              <div style={styles.infoBanner}>
                <strong>Selected Plan:</strong>{" "}
                {selectedUserPlan.basePlan ? "Base Plan" : "Custom Plan"} (
                {selectedUserPlan.minimumUser} -{" "}
                {selectedUserPlan.maximumUser > 0
                  ? selectedUserPlan.maximumUser
                  : "Unlimited"}{" "}
                users)
              </div>
            )}

            {pricingPlans.map((mainPlan) => (
              <div key={mainPlan.mainPlanId} style={styles.section}>
                <div style={styles.pricingPlanHeader}>
                  <h3 style={styles.mainPlanTitle}>{mainPlan.mainPlanName}</h3>
                  {mainPlan.adminLoginPricePerUser > 0 && (
                    <div style={styles.adminPriceTag}>
                      Admin Login: ₹{mainPlan.adminLoginPricePerUser}/user
                    </div>
                  )}
                </div>

                <div style={styles.modulesGrid}>
                  {mainPlan.moduleDtos.map((module) => (
                    <div
                      key={module.moduleId}
                      style={{
                        ...styles.moduleCard,
                        ...(selectedPricingModules.some(
                          (m) => m.moduleId === module.moduleId,
                        )
                          ? styles.moduleCardSelected
                          : {}),
                      }}
                      onClick={() => handlePricingModuleToggle(module)}
                    >
                      <label style={styles.moduleLabel}>
                        <input
                          type="checkbox"
                          checked={selectedPricingModules.some(
                            (m) => m.moduleId === module.moduleId,
                          )}
                          onChange={() => handlePricingModuleToggle(module)}
                          style={styles.checkbox}
                        />
                        <div style={styles.moduleContent}>
                          <div style={styles.moduleName}>
                            {module.moduleName}
                          </div>
                          <div style={styles.modulePrice}>
                            ₹{module.modulePricePerUser}/user
                          </div>
                          {module.moduleFeatures &&
                            module.moduleFeatures.length > 0 && (
                              <ul style={styles.featureList}>
                                {module.moduleFeatures
                                  .slice(0, 3)
                                  .map((feature) => (
                                    <li
                                      key={feature.moduleFeatureId}
                                      style={styles.featureItem}
                                    >
                                      {feature.featureName}
                                    </li>
                                  ))}
                                {module.moduleFeatures.length > 3 && (
                                  <li style={styles.featureItem}>
                                    +{module.moduleFeatures.length - 3} more
                                    features
                                  </li>
                                )}
                              </ul>
                            )}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>

                {mainPlan.totalPrice > 0 && (
                  <div style={styles.planTotalPrice}>
                    Estimated Total: ₹{mainPlan.totalPrice}
                  </div>
                )}
              </div>
            ))}

            <div style={styles.buttonGroup}>
              <button onClick={handlePreviousStep} style={styles.prevBtn}>
                Previous
              </button>
              <button
                onClick={handleNextStep}
                style={styles.nextBtn}
                disabled={selectedPricingModules.length === 0}
              >
                Next: Company Details
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Company Details */}
        {currentStep === 3 && (
          <div style={styles.stepContent}>
            <h2 style={styles.stepTitle}>Company Details</h2>

            {selectedUserPlan && (
              <div style={styles.infoBanner}>
                <strong>Selected Plan:</strong>{" "}
                {selectedUserPlan.basePlan ? "Base Plan" : "Custom Plan"} (
                {selectedUserPlan.minimumUser} -{" "}
                {selectedUserPlan.maximumUser > 0
                  ? selectedUserPlan.maximumUser
                  : "Unlimited"}{" "}
                users)
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Company Strength *
                {selectedUserPlan && (
                  <span style={styles.labelHint}>
                    (Min: {selectedUserPlan.minimumUser}
                    {selectedUserPlan.maximumUser > 0 &&
                      `, Max: ${selectedUserPlan.maximumUser}`}
                    )
                  </span>
                )}
              </label>
              <input
                type="number"
                value={companyDetails.companyStrength}
                onChange={(e) =>
                  setCompanyDetails((prev) => ({
                    ...prev,
                    companyStrength: parseInt(e.target.value) || 0,
                  }))
                }
                style={styles.input}
                min={selectedUserPlan?.minimumUser || 1}
                max={
                  selectedUserPlan?.maximumUser > 0
                    ? selectedUserPlan.maximumUser
                    : undefined
                }
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Number of Admin Users</label>
              <input
                type="number"
                value={companyDetails.adminUsers}
                onChange={(e) =>
                  setCompanyDetails((prev) => ({
                    ...prev,
                    adminUsers: parseInt(e.target.value) || 0,
                  }))
                }
                style={styles.input}
                min="0"
              />
            </div>

            <div style={styles.selectedModulesList}>
              <h4 style={styles.selectedModulesTitle}>Selected Modules:</h4>
              {selectedPricingModules.map((module) => (
                <div key={module.moduleId} style={styles.selectedModuleItem}>
                  • {module.moduleName} - ₹{module.modulePricePerUser}/user
                </div>
              ))}
            </div>

            <div style={styles.buttonGroup}>
              <button onClick={handlePreviousStep} style={styles.prevBtn}>
                Previous
              </button>
              <button onClick={handleNextStep} style={styles.nextBtn}>
                Next: View Quotation
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Quotation Summary */}
        {currentStep === 4 && quotation && (
          <div style={styles.stepContent}>
            <h2 style={styles.stepTitle}>Order Summary</h2>

            {/* Amazon-style Order Details */}
            <div style={styles.amazonSummary}>
              {/* Left Column - Order Details */}
              <div style={styles.amazonLeft}>
                <div style={styles.amazonSection}>
                  <h3 style={styles.amazonSectionTitle}>Review your order</h3>

                  <div style={styles.orderInfoCard}>
                    <div style={styles.orderInfoRow}>
                      <span style={styles.orderInfoLabel}>Plan Type:</span>
                      <span style={styles.orderInfoValue}>
                        {quotation.mainPlanName}
                      </span>
                    </div>
                    <div style={styles.orderInfoRow}>
                      <span style={styles.orderInfoLabel}>Subscription:</span>
                      <span style={styles.orderInfoValue}>
                        {quotation.subscriptionType}
                      </span>
                    </div>
                    <div style={styles.orderInfoRow}>
                      <span style={styles.orderInfoLabel}>Company Size:</span>
                      <span style={styles.orderInfoValue}>
                        {quotation.companyStrength} users
                      </span>
                    </div>
                    {quotation.baseUserPlan && (
                      <div style={styles.orderInfoRow}>
                        <span style={styles.orderInfoLabel}>Base Plan:</span>
                        <span style={styles.orderInfoValue}>
                          {quotation.baseUserPlaneStrength} users included
                        </span>
                      </div>
                    )}
                    <div style={styles.orderInfoRow}>
                      <span style={styles.orderInfoLabel}>Admin Users:</span>
                      <span style={styles.orderInfoValue}>
                        {quotation.adminUsers}
                      </span>
                    </div>
                  </div>

                  <div style={styles.amazonModulesSection}>
                    <h4 style={styles.amazonModulesTitle}>Selected Modules</h4>
                    {quotation.moduleDtos.map((module, index) => (
                      <div
                        key={module.moduleId}
                        style={styles.amazonModuleItem}
                      >
                        <span style={styles.amazonModuleNumber}>
                          {index + 1}.
                        </span>
                        <span style={styles.amazonModuleName}>
                          {module.moduleName}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - Price Summary */}
              <div style={styles.amazonRight}>
                <div style={styles.priceSummaryCard}>
                  <h3 style={styles.priceSummaryTitle}>Order Summary</h3>

                  <div style={styles.priceSummaryContent}>
                    {quotation.adminUserPricePerUser > 0 && (
                      <div style={styles.priceRow}>
                        <span style={styles.priceLabel}>
                          Admin Users Price:
                        </span>
                        <span style={styles.priceValue}>
                          ₹
                          {quotation.adminUserPricePerUser *
                            quotation.adminUsers}
                        </span>
                      </div>
                    )}

                    {quotation.serverCost > 0 && (
                      <div style={styles.priceRow}>
                        <span style={styles.priceLabel}>Server Cost:</span>
                        <span style={styles.priceValue}>
                          ₹{quotation.serverCost}
                        </span>
                      </div>
                    )}

                    <div style={styles.priceDivider}></div>

                    <div style={styles.totalPriceRow}>
                      <span style={styles.totalPriceLabel}>Order Total:</span>
                      <span style={styles.totalPriceValue}>
                        ₹{quotation.totalPrice.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={handleNextStep}
                    style={styles.amazonCheckoutBtn}
                  >
                    Proceed to Checkout
                  </button>

                  <div style={styles.amazonNote}>
                    By proceeding, you agree to our terms and conditions
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.buttonGroup}>
              <button onClick={handlePreviousStep} style={styles.prevBtn}>
                Previous
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Final Details */}
        {currentStep === 5 && quotation && (
          <div style={styles.stepContent}>
            <h2 style={styles.stepTitle}>Complete Your Order</h2>

            <div style={styles.formSection}>
              <h3 style={styles.sectionTitle}>Company Information</h3>

              <div style={styles.formGroup}>
                <label style={styles.label}>Company Name *</label>
                <input
                  type="text"
                  value={finalDetails.companyName}
                  onChange={(e) =>
                    setFinalDetails((prev) => ({
                      ...prev,
                      companyName: e.target.value,
                    }))
                  }
                  style={styles.input}
                  placeholder="Enter company name"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Company Email *</label>
                <input
                  type="email"
                  value={finalDetails.email}
                  onChange={(e) =>
                    setFinalDetails((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  style={styles.input}
                  placeholder="company@example.com"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Subdomain *</label>
                <div style={styles.subdomainWrapper}>
                  <input
                    type="text"
                    value={finalDetails.subDomain}
                    onChange={(e) =>
                      setFinalDetails((prev) => ({
                        ...prev,
                        subDomain: e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, ""),
                      }))
                    }
                    style={styles.input}
                    placeholder="yourcompany"
                    required
                  />
                  <span style={styles.domainSuffix}>.ourdept.com</span>
                </div>
                <small style={styles.hint}>
                  This will be your unique login URL
                </small>
              </div>
            </div>

            <div style={{ ...styles.formSection, ...styles.adminSection }}>
              <h3 style={styles.sectionTitle}>Primary Administrator</h3>

              <div style={styles.formGroup}>
                <label style={styles.label}>Admin Name *</label>
                <input
                  type="text"
                  value={finalDetails.userName}
                  onChange={(e) =>
                    setFinalDetails((prev) => ({
                      ...prev,
                      userName: e.target.value,
                      loginCredentialDTO: {
                        ...prev.loginCredentialDTO,
                        name: e.target.value,
                      },
                    }))
                  }
                  style={styles.input}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Admin Email *</label>
                <input
                  type="email"
                  value={finalDetails.loginCredentialDTO.email}
                  onChange={(e) =>
                    setFinalDetails((prev) => ({
                      ...prev,
                      loginCredentialDTO: {
                        ...prev.loginCredentialDTO,
                        email: e.target.value,
                      },
                    }))
                  }
                  style={styles.input}
                  placeholder="admin@company.com"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Password *</label>
                <div style={styles.passwordWrapper}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={finalDetails.loginCredentialDTO.password}
                    onChange={(e) =>
                      setFinalDetails((prev) => ({
                        ...prev,
                        loginCredentialDTO: {
                          ...prev.loginCredentialDTO,
                          password: e.target.value,
                        },
                      }))
                    }
                    style={styles.input}
                    placeholder="Minimum 6 characters"
                    minLength="6"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.passwordToggle}
                  >
                    {showPassword ? "👁️" : "🙈"}
                  </button>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Role</label>
                <select
                  value={finalDetails.role}
                  onChange={(e) =>
                    setFinalDetails((prev) => ({
                      ...prev,
                      role: e.target.value,
                    }))
                  }
                  style={styles.select}
                >
                  <option value="ADMIN">Admin</option>
                  <option value="SUPER_ADMIN">Super Admin</option>
                </select>
              </div>
            </div>

            <div style={styles.formSection}>
              <h3 style={styles.sectionTitle}>Subscription Details</h3>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Subscription Duration</label>
                  <select
                    value={finalDetails.monthPlan}
                    onChange={(e) => {
                      setFinalDetails((prev) => ({
                        ...prev,
                        monthPlan: e.target.value,
                      }));
                      calculateValidTill(e.target.value);
                    }}
                    style={styles.select}
                  >
                    <option value="THREE_MONTHS">3 Months</option>
                    <option value="SIX_MONTHS">6 Months</option>
                    <option value="TWELVE_MONTHS">12 Months</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Purchase Date</label>
                  <input
                    type="date"
                    value={finalDetails.purchasedAt}
                    onChange={(e) => {
                      setFinalDetails((prev) => ({
                        ...prev,
                        purchasedAt: e.target.value,
                      }));
                      calculateValidTill(finalDetails.monthPlan);
                    }}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Valid Till</label>
                <input
                  type="date"
                  value={finalDetails.validTill}
                  onChange={(e) =>
                    setFinalDetails((prev) => ({
                      ...prev,
                      validTill: e.target.value,
                    }))
                  }
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.formSection}>
              <h3 style={styles.sectionTitle}>Payment</h3>

              <div style={styles.paymentSummary}>
                <div style={styles.paymentRow}>
                  <span>Total Amount:</span>
                  <span style={styles.paymentTotal}>
                    ₹{quotation.totalPrice.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Amount Paid</label>
                <input
                  type="number"
                  value={finalDetails.amountPaid}
                  onChange={(e) =>
                    setFinalDetails((prev) => ({
                      ...prev,
                      amountPaid: parseFloat(e.target.value) || 0,
                    }))
                  }
                  style={styles.input}
                  min="0"
                  max={quotation.totalPrice}
                  placeholder="0"
                />
                <small style={styles.hint}>
                  Enter the amount received from customer
                </small>
              </div>
            </div>

            <div style={styles.buttonGroup}>
              <button onClick={handlePreviousStep} style={styles.prevBtn}>
                Previous
              </button>
              <button
                onClick={handleSubmit}
                style={styles.submitBtn}
                disabled={loading}
              >
                {loading ? "Creating Tenant..." : "Complete Order"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "900px",
    maxHeight: "90vh",
    overflow: "auto",
    position: "relative",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  },
  closeBtn: {
    position: "absolute",
    top: "20px",
    right: "20px",
    background: "none",
    border: "none",
    fontSize: "28px",
    cursor: "pointer",
    color: "#666",
    zIndex: 10,
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
  },
  progressBar: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    padding: "30px 20px 20px",
    borderBottom: "1px solid #e0e0e0",
  },
  progressStep: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#e0e0e0",
    color: "#999",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "600",
    transition: "all 0.3s",
  },
  progressStepActive: {
    backgroundColor: "#007bff",
    color: "#fff",
    transform: "scale(1.1)",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "5px solid #f3f3f3",
    borderTop: "5px solid #007bff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  stepContent: {
    padding: "30px 40px",
  },
  stepTitle: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#333",
    marginBottom: "30px",
    textAlign: "center",
  },
  section: {
    marginBottom: "30px",
  },
  sectionSubtitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "20px",
  },
  userPlansGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: "20px",
  },
  pricingPlanHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
  },
  adminPriceTag: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#ff6b6b",
    padding: "8px 15px",
    backgroundColor: "#ffe0e0",
    borderRadius: "8px",
  },
  planTotalPrice: {
    textAlign: "right",
    fontSize: "16px",
    fontWeight: "700",
    color: "#007bff",
    marginTop: "15px",
    padding: "10px",
    backgroundColor: "#f0f8ff",
    borderRadius: "8px",
  },
  moduleDescription: {
    fontSize: "13px",
    color: "#666",
    marginBottom: "10px",
  },
  planCard: {
    border: "2px solid #e0e0e0",
    borderRadius: "12px",
    padding: "20px",
    transition: "all 0.3s",
    cursor: "pointer",
  },
  planCardSelected: {
    borderColor: "#007bff",
    backgroundColor: "#f0f8ff",
  },
  planHeader: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },
  radio: {
    width: "20px",
    height: "20px",
    cursor: "pointer",
  },
  planInfo: {
    flex: 1,
  },
  badge: {
    fontSize: "12px",
    padding: "3px 8px",
    backgroundColor: "#28a745",
    color: "#fff",
    borderRadius: "12px",
    fontWeight: "500",
    marginTop: "5px",
    display: "inline-block",
  },
  planDetails: {
    fontSize: "14px",
    color: "#666",
  },
  mainPlanSection: {
    marginBottom: "30px",
  },
  mainPlanTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#007bff",
    marginBottom: "15px",
    paddingBottom: "10px",
    borderBottom: "2px solid #e0e0e0",
  },
  modulesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "15px",
  },
  moduleCard: {
    border: "2px solid #e0e0e0",
    borderRadius: "12px",
    padding: "15px",
    transition: "all 0.3s",
    cursor: "pointer",
    backgroundColor: "#fff",
  },
  moduleCardSelected: {
    borderColor: "#007bff",
    backgroundColor: "#f0f8ff",
  },
  moduleLabel: {
    display: "flex",
    gap: "12px",
    cursor: "pointer",
    alignItems: "flex-start",
  },
  checkbox: {
    width: "18px",
    height: "18px",
    marginTop: "2px",
    cursor: "pointer",
    flexShrink: 0,
  },
  moduleContent: {
    flex: 1,
  },
  moduleName: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "5px",
  },
  modulePrice: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#28a745",
    marginBottom: "8px",
  },
  // Add to your styles object:
  amazonSummary: {
    display: "flex",
    gap: "30px",
    marginTop: "20px",
  },
  amazonLeft: {
    flex: 2,
  },
  amazonRight: {
    flex: 1,
  },
  amazonSection: {
    marginBottom: "30px",
  },
  amazonSectionTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "20px",
  },
  orderInfoCard: {
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "20px",
  },
  orderInfoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid #f0f0f0",
  },
  orderInfoLabel: {
    fontSize: "14px",
    color: "#666",
  },
  orderInfoValue: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
  },
  amazonModulesSection: {
    backgroundColor: "#f9f9f9",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "15px",
  },
  amazonModulesTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "15px",
  },
  amazonModuleItem: {
    display: "flex",
    gap: "10px",
    padding: "8px 0",
    fontSize: "14px",
    color: "#007bff",
  },
  amazonModuleNumber: {
    fontWeight: "600",
  },
  amazonModuleName: {
    flex: 1,
  },
  priceSummaryCard: {
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "20px",
    position: "sticky",
    top: "20px",
  },
  priceSummaryTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "20px",
  },
  priceSummaryContent: {
    marginBottom: "20px",
  },
  priceRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0",
    fontSize: "14px",
  },
  priceLabel: {
    color: "#666",
  },
  priceValue: {
    fontWeight: "600",
    color: "#333",
  },
  priceDivider: {
    height: "1px",
    backgroundColor: "#e0e0e0",
    margin: "15px 0",
  },
  totalPriceRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "15px 0",
  },
  totalPriceLabel: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
  },
  totalPriceValue: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#B12704",
  },
  amazonCheckoutBtn: {
    width: "100%",
    padding: "15px",
    fontSize: "16px",
    fontWeight: "600",
    backgroundColor: "#FFD814",
    border: "1px solid #FCD200",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
    color: "#0F1111",
  },
  amazonNote: {
    fontSize: "12px",
    color: "#666",
    textAlign: "center",
    marginTop: "15px",
  },
  paymentSummary: {
    backgroundColor: "#f9f9f9",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "20px",
  },
  paymentRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
  },
  paymentTotal: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#B12704",
  },
  featureList: {
    margin: "10px 0 0 0",
    paddingLeft: "20px",
  },
  featureItem: {
    fontSize: "12px",
    color: "#666",
    marginBottom: "3px",
  },
  loadingText: {
    textAlign: "center",
    padding: "20px",
    color: "#666",
    fontSize: "14px",
  },
  infoBanner: {
    backgroundColor: "#e3f2fd",
    border: "1px solid #90caf9",
    borderRadius: "8px",
    padding: "15px",
    marginBottom: "20px",
    fontSize: "14px",
    color: "#1976d2",
  },
  formGroup: {
    marginBottom: "20px",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "8px",
  },
  labelHint: {
    fontWeight: "400",
    fontSize: "13px",
    color: "#666",
    marginLeft: "8px",
  },
  input: {
    width: "100%",
    padding: "12px 15px",
    fontSize: "15px",
    border: "1px solid #d0d0d0",
    borderRadius: "8px",
    boxSizing: "border-box",
    transition: "border-color 0.3s",
  },
  select: {
    width: "100%",
    padding: "12px 15px",
    fontSize: "15px",
    border: "1px solid #d0d0d0",
    borderRadius: "8px",
    boxSizing: "border-box",
    backgroundColor: "#fff",
    cursor: "pointer",
  },
  selectedModulesList: {
    backgroundColor: "#f9f9f9",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "15px",
    marginTop: "20px",
  },
  selectedModulesTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "10px",
  },
  selectedModuleItem: {
    fontSize: "14px",
    color: "#007bff",
    marginBottom: "5px",
  },
  summaryCard: {
    backgroundColor: "#f9f9f9",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    padding: "25px",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "12px 0",
    borderBottom: "1px solid #e0e0e0",
  },
  summaryLabel: {
    fontSize: "15px",
    color: "#333",
    fontWeight: "500",
  },
  summaryValue: {
    fontSize: "15px",
    color: "#666",
  },
  modulesList: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  summaryModule: {
    fontSize: "14px",
    color: "#007bff",
  },
  totalRow: {
    borderBottom: "none",
    marginTop: "10px",
  },
  totalPrice: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#333",
  },
  formSection: {
    marginBottom: "30px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "20px",
  },
  subdomainWrapper: {
    display: "flex",
    alignItems: "center",
  },
  domainSuffix: {
    marginLeft: "10px",
    fontSize: "15px",
    color: "#666",
  },
  formRow: {
    display: "flex",
    gap: "20px",
  },
  adminSection: {
    backgroundColor: "#f9f9f9",
    padding: "20px",
    borderRadius: "12px",
  },
  passwordWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  passwordToggle: {
    position: "absolute",
    right: "10px",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "18px",
    color: "#666",
  },
  hint: {
    fontSize: "12px",
    color: "#999",
    marginTop: "5px",
    display: "block",
  },
  buttonGroup: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "20px",
  },
  prevBtn: {
    padding: "12px 25px",
    fontSize: "15px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    backgroundColor: "#f0f0f0",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  nextBtn: {
    padding: "12px 25px",
    fontSize: "15px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#007bff",
    color: "#fff",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  submitBtn: {
    padding: "12px 25px",
    fontSize: "15px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#28a745",
    color: "#fff",
    cursor: "pointer",
    transition: "all 0.2s",
  },
};

export default PurchaseFlowModal;
